/**
 * 商談詳細「見積」タブ。
 *  - useDealQuotes でこの商談の見積一覧
 *  - 行クリックで quoteDetail へ
 *  - 「+ 書類作成」(見積/注文請書/納品書/請求書/同意書/契約書)
 *    ※ハンドラの実装は P-E。P-D ではボタンとメニューのスケルトンのみ
 */
import type { QuoteStatus } from '@goenhub/shared';
import { Calendar, CheckCircle, Copy, ExternalLink, FileText, Pencil, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { DocumentMailDialog } from '../components/DocumentMailDialog';
import { CreateQuoteDialog } from '../dialogs/CreateQuoteDialog';
import { WonLostDialog } from '../WonLostDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { type DealRecord } from '@/hooks/useDeals';
import { useDealQuotes, useUpdateQuoteDates, type QuoteRecord } from '@/hooks/useQuotes';
import {
  canEditDocumentDates,
  DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE,
} from '@/lib/documentDatePermission';
import { DocumentQuickActions } from '@/pages/sales-contacts/components/DocumentQuickActions';
import { paths } from '@/routes/paths';

const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: '下書き',
  sent: '送信済',
  expired: '期限切れ',
  won: '成約',
  lost: '失注',
};

const STATUS_VARIANT: Record<QuoteStatus, 'default' | 'draft' | 'sent' | 'danger' | 'approved'> = {
  draft: 'draft',
  sent: 'sent',
  expired: 'danger',
  won: 'approved',
  lost: 'danger',
};

interface Props {
  deal: DealRecord;
  recipientEmail?: string | null;
  /** Deal タブ間ジャンプ (NextActionBar 等から使う) */
  onJumpFulfillment?: () => void;
}

export function DealQuotesTab({ deal, recipientEmail, onJumpFulfillment }: Props) {
  const { data: quotes, isLoading } = useDealQuotes(deal.id);
  const updateQuoteDates = useUpdateQuoteDates();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [mailQuoteId, setMailQuoteId] = useState<string | null>(null);
  const [wonLostDialog, setWonLostDialog] = useState<{
    open: boolean;
    mode: 'won' | 'lost';
    quoteId: string | null;
    quoteLabel: string | null;
    quoteAmount: number | null;
  }>({
    open: false,
    mode: 'won',
    quoteId: null,
    quoteLabel: null,
    quoteAmount: null,
  });
  const [editingQuoteDates, setEditingQuoteDates] = useState<{
    quoteId: string;
    issuedAt: string;
    validUntil: string;
  } | null>(null);
  const [editingDraftQuote, setEditingDraftQuote] = useState<QuoteRecord | null>(null);

  // v3.1.7: Deal の status ではなく **各見積の status** で成約/失注ボタンの可否を判定。
  // これにより「Deal が closed (won) でも、新規追加した見積に対して成約/失注を確定できる」運用に対応。

  const copyPublicUrl = async (token: string) => {
    const url = `${window.location.origin}/quote/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('公開URLをコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  return (
    <div className="space-y-4">
      {/* ヘッダ */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">見積書</h3>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            {quotes && quotes.length > 0 ? `${quotes.length} 件発行済み` : 'まだ発行されていません'}
          </p>
        </div>
        <DocumentQuickActions
          dealId={deal.id}
          onCreateQuote={() => setCreateOpen(true)}
          onJumpFulfillment={onJumpFulfillment}
        />
      </div>

      {/* 一覧 */}
      {isLoading ? (
        <Spinner />
      ) : !quotes || quotes.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="見積書なし"
          description="「+ 書類作成」から見積書を発行できます。(作成 UI は近日対応)"
        />
      ) : (
        <div className="rounded-md border border-[var(--border)] overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
            <div>件名</div>
            <div>合計 (税込)</div>
            <div>発行日</div>
            <div>有効期限</div>
            <div>状態</div>
            <div className="text-right">操作</div>
          </div>
          {quotes.map((q, i) => (
            <div
              key={q.id}
              className={`grid grid-cols-[2fr_1fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-2.5 items-center text-sm ${
                i < quotes.length - 1 ? 'border-b border-[var(--border)]' : ''
              }`}
            >
              <Link
                to={paths.quoteDetail.replace(':dealId', deal.id).replace(':quoteId', q.id)}
                className="text-[var(--text-primary)] hover:text-[var(--accent)] hover:underline truncate"
              >
                {q.title || `見積 ${q.id.slice(0, 6)}`}
              </Link>
              <div className="tabular-nums">¥{q.totalIncludingTax.toLocaleString('ja-JP')}</div>
              <div className="text-xs text-[var(--text-muted)] tabular-nums">
                {fmtDate(q.issuedAt)}
              </div>
              <div className="text-xs text-[var(--text-muted)] tabular-nums">
                {fmtDate(q.validUntil)}
              </div>
              <div>
                <Badge variant={STATUS_VARIANT[q.status]}>{STATUS_LABEL[q.status]}</Badge>
              </div>
              <div className="flex items-center gap-1 justify-end flex-wrap">
                <button
                  type="button"
                  onClick={() => copyPublicUrl(q.publicToken)}
                  className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]"
                  title="公開URLをコピー"
                >
                  <Copy className="size-3.5" />
                </button>
                {canEditDocumentDates(user, deal, q, 'quote') && (
                  <button
                    type="button"
                    onClick={() => {
                      const issued = toJsDate(q.issuedAt);
                      const valid = toJsDate(q.validUntil);
                      const fmt = (d: Date | null) =>
                        d
                          ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                          : '';
                      setEditingQuoteDates({
                        quoteId: q.id,
                        issuedAt: fmt(issued),
                        validUntil: fmt(valid),
                      });
                    }}
                    className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]"
                    title="発行日 / 有効期限を編集"
                  >
                    <Calendar className="size-3.5" />
                  </button>
                )}
                {q.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => setEditingDraftQuote(q)}
                    className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)]"
                    title="見積内容を編集（下書きのみ）"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                )}
                <Button variant="secondary" size="sm" onClick={() => setMailQuoteId(q.id)}>
                  <ExternalLink className="size-3" />
                  {q.status === 'sent' ? '再送信' : 'メール送信'}
                </Button>
                {q.status !== 'won' && q.status !== 'lost' && (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() =>
                        setWonLostDialog({
                          open: true,
                          mode: 'won',
                          quoteId: q.id,
                          quoteLabel: q.title || `見積 ${q.id.slice(0, 6)}`,
                          quoteAmount: q.totalIncludingTax,
                        })
                      }
                      title="この見積で成約確定"
                    >
                      <CheckCircle className="size-3" />
                      成約
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() =>
                        setWonLostDialog({
                          open: true,
                          mode: 'lost',
                          quoteId: q.id,
                          quoteLabel: q.title || `見積 ${q.id.slice(0, 6)}`,
                          quoteAmount: q.totalIncludingTax,
                        })
                      }
                      title="この見積で失注確定"
                    >
                      <XCircle className="size-3" />
                      失注
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateQuoteDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        dealId={deal.id}
        contactId={deal.contactId}
        defaultRecipientEmail={recipientEmail ?? null}
        defaultTitle={deal.title ? `${deal.title} お見積書` : undefined}
        onCreatedForSend={(quoteId) => setMailQuoteId(quoteId)}
      />

      <CreateQuoteDialog
        open={editingDraftQuote !== null}
        onOpenChange={(o) => {
          if (!o) setEditingDraftQuote(null);
        }}
        dealId={deal.id}
        contactId={deal.contactId}
        defaultRecipientEmail={recipientEmail ?? null}
        editingQuote={
          editingDraftQuote ? { id: editingDraftQuote.id, data: editingDraftQuote } : null
        }
        onCreatedForSend={(quoteId) => setMailQuoteId(quoteId)}
      />

      <DocumentMailDialog
        open={mailQuoteId !== null}
        onOpenChange={(o) => !o && setMailQuoteId(null)}
        documentType="quote"
        dealId={deal.id}
        documentId={mailQuoteId}
      />

      <WonLostDialog
        open={wonLostDialog.open}
        mode={wonLostDialog.mode}
        dealId={deal.id}
        quoteId={wonLostDialog.quoteId}
        quoteLabel={wonLostDialog.quoteLabel}
        initialAmount={wonLostDialog.quoteAmount ?? deal.expectedAmount}
        onOpenChange={(o) => setWonLostDialog((s) => ({ ...s, open: o }))}
      />

      <Dialog
        open={editingQuoteDates !== null}
        onOpenChange={(o) => !o && setEditingQuoteDates(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>見積書の日付を編集</DialogTitle>
          </DialogHeader>
          {editingQuoteDates && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">発行日</Label>
                <Input
                  type="date"
                  value={editingQuoteDates.issuedAt}
                  onChange={(e) =>
                    setEditingQuoteDates({
                      ...editingQuoteDates,
                      issuedAt: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label className="text-xs">有効期限</Label>
                <Input
                  type="date"
                  value={editingQuoteDates.validUntil}
                  onChange={(e) =>
                    setEditingQuoteDates({
                      ...editingQuoteDates,
                      validUntil: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditingQuoteDates(null)}
              disabled={updateQuoteDates.isPending}
            >
              キャンセル
            </Button>
            <Button
              variant="primary"
              disabled={updateQuoteDates.isPending}
              onClick={async () => {
                if (!editingQuoteDates) return;
                try {
                  await updateQuoteDates.mutateAsync({
                    dealId: deal.id,
                    quoteId: editingQuoteDates.quoteId,
                    issuedAt: editingQuoteDates.issuedAt
                      ? new Date(editingQuoteDates.issuedAt)
                      : null,
                    validUntil: editingQuoteDates.validUntil
                      ? new Date(editingQuoteDates.validUntil)
                      : null,
                  });
                  toast.success('日付を更新しました');
                  setEditingQuoteDates(null);
                } catch (err) {
                  const code = (err as { code?: string }).code ?? '';
                  toast.error(
                    code === 'permission-denied'
                      ? DOCUMENT_DATE_PERMISSION_DENIED_MESSAGE
                      : `日付の更新に失敗しました: ${(err as Error).message}`,
                  );
                }
              }}
            >
              {updateQuoteDates.isPending ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function toJsDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}
